import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  completeFanNewPasswordChallenge,
  FanNewPasswordRequiredError,
  signInAsFan,
  signInFanWithPassword,
} from '../auth/fan-auth';
import { useI18n } from '../i18n';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { AuthShell } from './auth/AuthShell';
import { friendlyFanAuthError } from './auth/errorMessages';
import {
  errorBox,
  inputClass,
  labelClass,
  primaryButton,
} from './auth/formStyles';

/**
 * Fan-side sign-in: email + password (SRP), no Hosted UI redirect.
 * Handles the NEW_PASSWORD_REQUIRED challenge inline (admin-created
 * accounts or force-reset flow) by swapping the form for a single
 * new-password field that completes the same in-flight CognitoUser.
 *
 * On success, the page navigates to "/" (Landing) so the BottomNav
 * + AppHeader can pick up the new signed-in state.
 */
export function Login() {
  const { t } = useI18n();
  useDocumentTitle(t('auth.login.title'));
  const nav = useNavigate();
  const [params] = useSearchParams();
  // Where to bounce the user after sign-in succeeds. Set by fan-api's
  // 401 handler so an expired-token kick lands them back where they
  // were. Guarded against open-redirect by requiring a leading '/'.
  const rawFrom = params.get('from');
  const fromPath = rawFrom && rawFrom.startsWith('/') && !rawFrom.startsWith('//') ? rawFrom : '/';

  // Per `nfc-point-cards` — resume a pending card redemption stashed by
  // /redeem before the login bounce. Previously only the OAuth callback
  // (SignInCallback) consumed this, so email/password sign-ins dropped
  // the fan on the home page and the card code was lost.
  function postLoginDestination(): string {
    try {
      const pending = sessionStorage.getItem('pendingRedeemCode');
      if (pending) {
        sessionStorage.removeItem('pendingRedeemCode');
        return `/redeem?key=${encodeURIComponent(pending)}`;
      }
    } catch { /* sessionStorage unavailable — fall through */ }
    return fromPath;
  }
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newPasswordUser, setNewPasswordUser] = useState<unknown | null>(null);
  const [newPassword, setNewPassword] = useState('');

  async function onSignIn(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !password) return;
    setPending(true);
    setError(null);
    try {
      await signInFanWithPassword(email.trim(), password);
      nav(postLoginDestination(), { replace: true });
    } catch (err) {
      if (err instanceof FanNewPasswordRequiredError) {
        setNewPasswordUser(err.user);
        setError(null);
      } else {
        setError(friendlyFanAuthError(err, t));
      }
    } finally {
      setPending(false);
    }
  }

  async function onSetNewPassword(e: React.FormEvent) {
    e.preventDefault();
    if (!newPasswordUser || !newPassword) return;
    setPending(true);
    setError(null);
    try {
      await completeFanNewPasswordChallenge(newPasswordUser, newPassword);
      nav(postLoginDestination(), { replace: true });
    } catch (err) {
      setError(friendlyFanAuthError(err, t));
    } finally {
      setPending(false);
    }
  }

  if (newPasswordUser) {
    return (
      <AuthShell
        kicker={t('auth.login.kicker')}
        title={t('auth.login.newPasswordRequired')}
        subtitle={t('auth.login.newPasswordHint')}
      >
        <form onSubmit={onSetNewPassword} className="space-y-3">
          <div>
            <label htmlFor="np" className={labelClass}>{t('auth.login.newPasswordLabel')}</label>
            <input
              id="np"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder={t('auth.login.passwordPlaceholder')}
              autoComplete="new-password"
              autoFocus
              required
              className={inputClass}
            />
          </div>
          <button type="submit" disabled={pending} className={primaryButton}>
            {pending ? t('auth.login.submitting') : t('auth.login.submit')}
          </button>
        </form>
        {error && <p role="alert" className={errorBox}>{error}</p>}
      </AuthShell>
    );
  }

  return (
    <AuthShell
      kicker={t('auth.login.kicker')}
      title={t('auth.login.title')}
      subtitle={t('auth.login.subtitle')}
      footer={
        <div className="space-y-2 text-center text-[11px] text-slate-500">
          <p>
            {t('auth.login.noAccount')}{' '}
            <Link to="/signup" className="font-medium text-violet-300 underline hover:text-violet-200">
              {t('auth.login.signupLink')}
            </Link>
          </p>
          <p>
            {t('auth.login.needConfirm')}{' '}
            <Link to="/confirm-account" className="underline hover:text-slate-300">
              {t('auth.login.confirmLink')}
            </Link>
          </p>
        </div>
      }
    >
      <form onSubmit={onSignIn} className="space-y-3">
        <div>
          <label htmlFor="email" className={labelClass}>{t('auth.login.emailLabel')}</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('auth.login.emailPlaceholder')}
            autoComplete="email"
            autoFocus
            required
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="pw" className={labelClass}>{t('auth.login.passwordLabel')}</label>
          <input
            id="pw"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('auth.login.passwordPlaceholder')}
            autoComplete="current-password"
            required
            className={inputClass}
          />
        </div>
        <div className="flex justify-end">
          <Link
            to="/forgot-password"
            className="text-[11px] text-slate-400 underline-offset-4 hover:text-slate-200 hover:underline"
          >
            {t('auth.login.forgotPassword')}
          </Link>
        </div>
        <button type="submit" disabled={pending} className={primaryButton}>
          {pending ? t('auth.login.submitting') : t('auth.login.submit')}
        </button>
      </form>
      {/* Per fan-google-sso — bounces through Cognito Hosted UI with
          `identity_provider=Google`, skipping the landing page so the
          fan goes straight to Google's consent screen. */}
      <div className="mt-4 pt-4 border-t border-slate-800">
        <div className="relative -mt-7 mb-3 flex justify-center">
          <span className="bg-slate-950 px-3 text-[11px] uppercase tracking-wider text-slate-500">或</span>
        </div>
        <button
          type="button"
          onClick={() => { void signInAsFan({ identityProvider: 'Google' }); }}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-700 bg-slate-900 px-4 py-2.5 text-sm text-slate-100 hover:border-violet-500/40 hover:bg-slate-800"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.85 0-5.27-1.92-6.13-4.51H2.18v2.84A11 11 0 0 0 12 23z" />
            <path fill="#FBBC05" d="M5.87 14.12a6.6 6.6 0 0 1-.34-2.12c0-.74.13-1.46.34-2.12V7.04H2.18A11 11 0 0 0 1 12c0 1.78.43 3.46 1.18 4.96l3.69-2.84z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.04l3.69 2.84C6.73 7.3 9.15 5.38 12 5.38z" />
          </svg>
          用 Google 登入
        </button>
      </div>
      {error && <p role="alert" className={errorBox}>{error}</p>}
    </AuthShell>
  );
}
